<script lang="ts" setup>
    import '../../../css/choice.css';
    import {popAlert,ModalStyles} from '../../lib/Notification';   
    import {onMounted,ref,reactive,computed} from 'vue';
    import {hideWindowObject} from '../../lib/Models/CommonItemsModel';   
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import {NoTzDate ,getNoTzToday} from "../../lib/DateUtils" 
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import {IPaymentReportInputs,IPaymentPlanPayReport} from '../../lib/Models/PaymentPlanModel';  
    import {ErrorReturnMessage,getPaymentReport} from '../../lib/Api/PaymentPlan';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import ChoiceTable from "../../components/ChoiceTable.vue"; 
    import {ICol, RenderModeType}  from "../../lib/ChoiceTableHelpers" ; 
    import {ButtonDisplaySize,ButtonDisplayStyle,AlignmentType} from '../../Enums/UiEnums';
    import {ActionButtonStyle} from '../../lib/Types/Types';  
      
    const reportTable = ref<InstanceType<typeof ChoiceTable>>();
    const reportData = ref<(IPaymentPlanPayReport)[]>([]);
    const reportColumns = ref<ICol[]>([ 
        { Name: 'Key', DataField: 'recordKey' }, 
        { Name: 'Total', DataField: 'totalAmount',Alignment: AlignmentType.Right,RenderMode: RenderModeType.AccountingMoney },
        { Name: 'Installment', DataField: 'installmentAmount',Alignment: AlignmentType.Right,RenderMode: RenderModeType.AccountingMoney },
        { Name: 'Status', DataField: 'statusDescription' }, 
        { Name: 'Chart', DataField: 'patientID' },
        { Name: 'Patient Name', DataField: 'patientName' },
        { Name: 'Payment', DataField: 'paymentAmount',Alignment: AlignmentType.Right,RenderMode: RenderModeType.AccountingMoney }, 
        { Name: 'Payment Date', DataField: 'paymentDate',Alignment: AlignmentType.Center },
        { Name: 'Local Date', DataField: 'localTime',Alignment: AlignmentType.Center },
        { Name: 'Error', DataField: 'errorFlag',Alignment: AlignmentType.Center },
        { Name: 'Message', DataField: 'paymentMessage' },
        { Name: 'Details', DataField: 'paymentDetails' }, 
    ]);
    const exportPrintDisabled = computed<boolean>(() => (reportData.value.length === 0));
    const formData = reactive <
    {       
        Search: {ErrorsOnly: boolean, StartDate: NoTzDate, EndDate: NoTzDate}
    }>({     
        Search: {ErrorsOnly: true, StartDate: getNoTzToday(), EndDate: getNoTzToday()}
    });
    const props = withDefaults(defineProps<{  
        readEnabled: boolean
    }>(),
    {
        readEnabled: false,
    });
    onMounted(async () => { 
        hideWindowObject("LoadingMessage");
    });
    function clearSearchData() : void {  
        reportData.value = [];
    } 
    async function peformSearch() : Promise<void> {  
        await getReportData();
    }  
    async function closeWindow() : Promise<void> {  
        window.close();
    }   
    async function printReport() : Promise<void> {  
        window.print();
    }    
    async function exportReport() : Promise<void> {  
        try {
            const wb = XLSX.utils.book_new()
            const ws1 = XLSX.utils.json_to_sheet(reportData.value);
            XLSX.utils.book_append_sheet(wb, ws1, "PaymentPlan");
            XLSX.writeFile(wb, "PaymentPlanReportData.xls");
        }
        catch (e) {
            await popAlert("Error downloading data to Excel: " + e);
        }	
    } 
    async function getReportData() : Promise<void> { 
        const reportInputs : IPaymentReportInputs = {errorFlag: formData.Search.ErrorsOnly, startDate: formData.Search.StartDate, endDate: formData.Search.EndDate}
        const returnResults = await getPaymentReport(reportInputs); 
        const messageTitle = "Get Report";
        if (!returnResults.success) {  
            await popAlert(returnResults.error?.error ?? ErrorReturnMessage.reportDisplay,messageTitle,ModalStyles.danger);
            return;
        } 
        reportData.value = returnResults.success ?? [];
    }
    function clickCheckbox(): void {
        const checkbox = document.querySelector<HTMLInputElement>('input[type="checkbox"]');
        if (checkbox) {
            checkbox.click();
        }
    }
</script> 

<style scoped>
    @import url('../../../css/paymentPlanGrid.css');
</style>

<template> 
    <WindowHeaderFooter
    :headerHeight="3.5"
    :footerHeight="3.5"
    currentPathOveride="officemd/App/billing/PaySimpleCreditCard">
        <template v-slot:WindowHeader>
            <div class="isalus-page-title-large pt-2 pb-2">Payments Report</div>
        </template>
        <template v-slot:WindowBody>
            <div class="row  label-isalus-small"> 
                <div class="grid-container-report_search col-9">
                    <div class="label-search-details">Date Range:</div>
                    <div>
                        <DatePickerNoTZ
                            v-model:value="formData.Search.StartDate" 
                            :clearable="false" 
                            :required=true
                            :errorDisplay=false 
                            :placeholder="'Search Start Date'"
                            :cssFramework="'vanilla'"
                            @update:value="()=>clearSearchData()" 
                        />
                    </div>  
                    <div>
                        <DatePickerNoTZ
                            v-model:value="formData.Search.EndDate" 
                            :clearable="false" 
                            :required=true
                            :errorDisplay=false 
                            :placeholder="'Search End Date'"
                            :cssFramework="'vanilla'"
                            @update:value="()=>clearSearchData()" 
                        /> 
                    </div>
                    <div class="pt-2">
                        <Checkbox ref="errorsOnlyCheckbox"
                            v-model="formData.Search.ErrorsOnly"
                            :title="'Return only those payments with errors.'"
                            @update:modelValue="()=>peformSearch()" />  
                        <span label class="ps-1 pe-3" @click="clickCheckbox">Errors Only</span>  
                    </div> 
                </div>
            </div>
            <p style="font-size: .75rem;" class="pt-2">
                <ChoiceTable 
                    ref="reportTable"
                    :columns="reportColumns" 
                    :data="reportData" 
                    :show-filters="false" 
                    :show-footer="false" 
                    :allowSorting="true"
                    :key-value-data-member="'recordKey'">
                </ChoiceTable>
            </p>
        </template>
        <template v-slot:WindowFooter>
            <div class="text-end ">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Search"
                    :text="'Search'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="peformSearch"
                    :endingPadding="1"/> 
                <ActionButtonV2 
                    :action="ActionButtonStyle.Excel"
                    :text="'Export'"
                    :disabled="exportPrintDisabled"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="exportReport"
                    :endingPadding="1"/> 
                <ActionButtonV2 
                    :action="ActionButtonStyle.Print"
                    :text="'Print'"
                    :disabled="exportPrintDisabled"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="printReport"
                    :endingPadding="1"/>  
                <ActionButtonV2 
                    :action="ActionButtonStyle.Close"
                    :text="'Close'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="closeWindow"/> 
            </div>
        </template>
    </WindowHeaderFooter> 
</template>
